import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import {
  getActivationData,
  getAppSettingsData,
  getPhoneBooksEntity,
  type MenuWordEntity,
  type PhoneBooksEntity,
  type SettingMenuButtonEntity,
} from "~/utils/receptionDB";
import {
  ButtonType,
  CallType,
  PlanType,
  NotificationType,
  ScreenType,
} from "~/utils/receptionDB.types";
import { useAtom } from "jotai";
import { languageAtom, receptionButtonTypeAtom, receptionCompanyIdAtom } from "~/Atom";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { buildErrorPath, mapErrorCode } from "~/utils/error";
import { ROUTE } from "~/constants/routes";

type RequiredFlags = {
  company: boolean;
  visitorName: boolean;
  requirement: boolean;
};

type Labels = {
  company: string;
  visitorName: string;
  requirement: string;
};

const DEFAULT_ERROR_CODE = "P8999";
const COMPANY_SETTINGS_NOT_FOUND = "P2006";
const NO_CALL_DESTINATION = "P2009";

const resolveGroupIdFromDisplayId = (
  phoneBooksEntity: PhoneBooksEntity | undefined,
  groupDisplayId?: number
): number | undefined => {
  if (groupDisplayId === undefined || groupDisplayId === 0) return undefined;
  return phoneBooksEntity?.groupList.find((g) => g.displayId === groupDisplayId)?.groupId;
};

/**
 * 来訪者情報（会社名/氏名/用件）を入力し、受付ボタン種別に応じて次画面/API を分岐する画面。
 */
// eslint-disable-next-line complexity
const VisitorInfoInputScreen: React.FC = () => {
  const [companyName, setCompanyName] = useState("");
  const [visitorName, setVisitorName] = useState("");
  const [requirement, setRequirement] = useState("");
  const navigate = useNavigate();
  const [language] = useAtom(languageAtom);
  const [receptionCompanyId] = useAtom(receptionCompanyIdAtom);
  const [receptionButtonType] = useAtom(receptionButtonTypeAtom);
  const { t } = useTranslation();
  const [localLabels, setLocalLabels] = useState<Partial<Labels>>({});
  const [placeholderCompany, setPlaceholderCompany] = useState("");
  const [placeholderFullName, setPlaceholderFullName] = useState("");
  const [placeholderRequirement, setPlaceholderRequirement] = useState("");
  const [localRequired, setLocalRequired] = useState<RequiredFlags | null>(null);
  const [showRequirementFieldFromSettings, setShowRequirementFieldFromSettings] = useState<
    boolean | null
  >(null);

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const generalReceptionUrl = buildIfaceApiUrl("v1/receptions/general");
  const buttonType = (receptionButtonType ?? "").trim().toUpperCase();
  const companyId = receptionCompanyId;

  const getMenuButtonConfig = (
    buttons: SettingMenuButtonEntity[],
    groupDisplayId?: number
  ): SettingMenuButtonEntity | undefined => {
    if (groupDisplayId !== undefined) {
      const matched = buttons.find((b) => b.id === groupDisplayId);
      if (matched) return matched;
    }
    return buttons.find((b) => b.id === 0) ?? buttons[0];
  };

  const resolveNotificationType = (
    buttonType: string,
    config: SettingMenuButtonEntity
  ): string | undefined => {
    const notificationKeyByType: Partial<Record<ButtonType, keyof SettingMenuButtonEntity>> = {
      [ButtonType.RECRUIT]: "recruitNotificationType",
      [ButtonType.OTHER]: "otherNotificationType",
      [ButtonType.SECRETARY]: "secretaryNotificationType",
    };
    const key = notificationKeyByType[buttonType as ButtonType];
    return key ? String(config[key]) : undefined;
  };

  const resolveCallDestination = (
    buttonType: string,
    config: SettingMenuButtonEntity
  ): { callType?: string; extensionNumber?: string; faceTimeAddress?: string } => {
    const destinationByType: Partial<
      Record<ButtonType, { callType: string; extensionNumber: string; faceTimeAddress: string }>
    > = {
      [ButtonType.RECRUIT]: {
        callType: config.recruitCallType,
        extensionNumber: config.recruitExtensionNumber,
        faceTimeAddress: config.recruitFaceTimeAddress,
      },
      [ButtonType.OTHER]: {
        callType: config.otherCallType,
        extensionNumber: config.otherExtensionNumber,
        faceTimeAddress: config.otherFaceTimeAddress,
      },
      [ButtonType.SECRETARY]: {
        callType: config.secretaryCallType,
        extensionNumber: config.secretaryExtensionNumber,
        faceTimeAddress: config.secretaryFaceTimeAddress,
      },
    };

    const resolved = destinationByType[buttonType as ButtonType];
    if (!resolved) {
      return {};
    }

    return resolved;
  };

  const hasCallDestination = (destination: {
    callType?: string;
    extensionNumber?: string;
    faceTimeAddress?: string;
  }) => {
    const callType = destination.callType?.toUpperCase();
    if (callType === CallType.Phone) return !!destination.extensionNumber?.trim();
    if (callType === CallType.FaceTime) return false;
    return false;
  };

  const resolveButtonTitle = (
    menuWordEntities: MenuWordEntity[],
    buttonType: string,
    languageCode: string,
    groupDisplayId?: number
  ): string | undefined => {
    const targetId = groupDisplayId ?? 0;
    const menuWordEntity =
      menuWordEntities.find((entity) => entity.id === targetId) ??
      menuWordEntities.find((entity) => entity.id === 0) ??
      menuWordEntities[0];

    if (!menuWordEntity || menuWordEntity.items.length === 0) return undefined;

    const normalizedLanguage = languageCode.toLowerCase();
    const localizedMenuWord =
      menuWordEntity.items.find((item) =>
        normalizedLanguage.startsWith(item.languageCode.toLowerCase())
      ) ?? menuWordEntity.items[0];

    if (!localizedMenuWord) return undefined;

    type MenuButtonTitleKey =
      | "groupButtonTitle"
      | "userButtonTitle"
      | "recruitButtonTitle"
      | "deliveryButtonTitle"
      | "taxiButtonTitle"
      | "secretaryButtonTitle"
      | "otherButtonTitle"
      | "qrCodeButtonTitle"
      | "receptionNumberButtonTitle"
      | "faceRecognitionButtonTitle";

    const titleKeyByType: Partial<Record<ButtonType, MenuButtonTitleKey>> = {
      [ButtonType.GROUP]: "groupButtonTitle",
      [ButtonType.USER]: "userButtonTitle",
      [ButtonType.RECRUIT]: "recruitButtonTitle",
      [ButtonType.DELIVERY]: "deliveryButtonTitle",
      [ButtonType.TAXI]: "taxiButtonTitle",
      [ButtonType.SECRETARY]: "secretaryButtonTitle",
      [ButtonType.OTHER]: "otherButtonTitle",
      [ButtonType.QR]: "qrCodeButtonTitle",
      [ButtonType.RECEPTIONCODE]: "receptionNumberButtonTitle",
      [ButtonType.FACERECOGNITION]: "faceRecognitionButtonTitle",
    };

    const key = titleKeyByType[buttonType as ButtonType];
    return key ? localizedMenuWord.menuWords[key] : undefined;
  };

  const navigateToError = (uiCode: string) => {
    navigate(buildErrorPath(uiCode));
  };

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  useEffect(() => {
    let mounted = true;

    // eslint-disable-next-line complexity
    const applySettings = async () => {
      try {
        const stored = await getAppSettingsData();
        if (!mounted || !stored) return;

        const basics = stored.basics ?? [];
        const basic =
          (companyId !== undefined ? basics.find((b) => b.id === companyId) : undefined) ??
          basics[0];

        if (basic) {
          setLocalRequired({
            company: !!basic.isRequiredCompanyName,
            visitorName: !!basic.isRequiredName,
            requirement: !!basic.isRequiredRequirement,
          });
          setShowRequirementFieldFromSettings(!!basic.isDisplayRequirement);
        }

        const items = stored.receptionCommonWord?.items ?? [];
        const matched = items.find((it) => language.startsWith(it.languageCode));
        if (matched) {
          const sw = matched.screenWords;
          const companyLabel =
            buttonType === ButtonType.RECRUIT ? sw.schoolNameTitle : sw.companyNameTitle;
          setLocalLabels({
            company: companyLabel,
            visitorName: sw.nameTitle,
            requirement: sw.requirementTitle,
          });

          setPlaceholderCompany(sw.companyNamePlaceHolderMessage);
          setPlaceholderFullName(sw.namePlaceHolderMessage);
          setPlaceholderRequirement(sw.requirementPlaceHolderMessage);
        }
      } catch (error) {
        console.error("Failed to load appSettings for visitor input:", error);
      }
    };

    void applySettings();

    return () => {
      mounted = false;
    };
  }, [buttonType, companyId, language]);

  const { playWithCooldown } = usePlayVoiceWithCooldown({ cooldownMs: 2000 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_LENGTH = 256;
  const companyTooLong = companyName.length > MAX_LENGTH;
  const visitorNameTooLong = visitorName.length > MAX_LENGTH;
  const requirementTooLong = requirement.length > MAX_LENGTH;

  useEffect(() => {
    const guidanceText = t("visitorInfo.title");

    void playWithCooldown(guidanceText).catch((err) => console.error("playVoice failed:", err));
  }, [playWithCooldown]);
  const effectiveRequired: RequiredFlags = localRequired ?? {
    company: true,
    visitorName: true,
    requirement: true,
  };
  const shouldShowRequirement = showRequirementFieldFromSettings ?? true;

  const isValid = () => {
    if (effectiveRequired.company && companyName.trim() === "") return false;
    if (effectiveRequired.visitorName && visitorName.trim() === "") return false;
    if (shouldShowRequirement && effectiveRequired.requirement && requirement.trim() === "")
      return false;

    if (companyTooLong) return false;
    if (visitorNameTooLong) return false;
    if (shouldShowRequirement && requirementTooLong) return false;
    return true;
  };

  const getForwardState = () => ({
    visitorName: visitorName.trim() || undefined,
    companyName: companyName.trim() || undefined,
    requirement: requirement.trim() || undefined,
  });

  const navigateForward = (path: string) => {
    navigate(path, {
      state: getForwardState(),
    });
  };

  const resolveErrorCode = (error: unknown): string => {
    const rawCode = (error as { response?: { data?: { errorCode?: number | string } } })?.response
      ?.data?.errorCode;

    if (typeof rawCode === "number") {
      return mapErrorCode(ScreenType.VISITOR_INFO_INPUT, rawCode);
    }

    if (typeof rawCode === "string") {
      const trimmed = rawCode.trim();
      if (!trimmed) return DEFAULT_ERROR_CODE;

      const numericCode = Number(trimmed);
      if (!Number.isNaN(numericCode)) {
        return mapErrorCode(ScreenType.VISITOR_INFO_INPUT, numericCode);
      }

      return trimmed;
    }

    return DEFAULT_ERROR_CODE;
  };

  const postGeneralReception = async (
    buttonType: string,
    notificationType: string,
    resolvedButtonName?: string,
    groupId?: number
  ) => {
    const body: Record<string, unknown> = {
      route: buttonType,
      affiliation: companyName.trim() || undefined,
      visitorName: visitorName.trim() || undefined,
      business: requirement.trim() || undefined,
      buttonName: resolvedButtonName,
      notificationType,
      groupId,
    };

    const resp = await apiClient.post<{ data?: { isChatSuccess?: boolean } }>(
      generalReceptionUrl,
      body,
      {
        headers: createIfaceHeaders(true),
      }
    );
    return resp?.data?.data?.isChatSuccess;
  };

  const handleCallFlow = (
    buttonType: string,
    plan: string,
    notificationType: string,
    menuButton: SettingMenuButtonEntity,
    destinationConfigured: boolean
  ) => {
    const isCallPlan = plan === PlanType.CALL || plan === PlanType.CHAT_CALL;
    if (!isCallPlan || notificationType !== NotificationType.Call) return false;

    if (buttonType === ButtonType.OTHER && menuButton.isDisplayInputExtensionNumber) {
      navigateForward(ROUTE.RECEPTION.INPUT_EXTENSION_NUMBER);
      return true;
    }

    if (!destinationConfigured) {
      navigateToError(NO_CALL_DESTINATION);
      return true;
    }

    navigateForward(ROUTE.RECEPTION.CALLING);
    return true;
  };

  const navigateChatAndCallResult = (destinationConfigured: boolean) => {
    if (!destinationConfigured) {
      navigateToError(NO_CALL_DESTINATION);
      return true;
    }

    navigateForward(ROUTE.RECEPTION.CALLING);
    return true;
  };

  const handleChatFlow = async (
    buttonType: string,
    plan: string,
    notificationType: string,
    destinationConfigured: boolean,
    resolvedButtonName?: string,
    groupId?: number
  ) => {
    const isChatPlan = plan === PlanType.CHAT || plan === PlanType.CHAT_CALL;
    const isChatNotification =
      notificationType === NotificationType.Chat ||
      notificationType === NotificationType.ChatAndCall;
    if (!isChatPlan || !isChatNotification) return false;

    try {
      const isChatSuccess = await postGeneralReception(
        buttonType,
        notificationType,
        resolvedButtonName,
        groupId
      );

      if (notificationType === NotificationType.Chat) {
        if (isChatSuccess === false) {
          navigateToError(DEFAULT_ERROR_CODE);
          return true;
        }
        navigateForward(ROUTE.RECEPTION.COMPLETE);
        return true;
      }

      return navigateChatAndCallResult(destinationConfigured);
    } catch (error) {
      if (notificationType === NotificationType.Chat) {
        navigateToError(resolveErrorCode(error));
        return true;
      }

      return navigateChatAndCallResult(destinationConfigured);
    }
  };

  // eslint-disable-next-line complexity
  const handleOk = async () => {
    if (!isValid()) return;
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (buttonType === ButtonType.GROUP) {
      navigateForward(ROUTE.RECEPTION.GROUP_LIST);
      return;
    }

    if (buttonType === ButtonType.USER) {
      navigateForward(ROUTE.RECEPTION.SEARCH_USER);
      return;
    }

    try {
      const [activation, appSettings, phoneBooksEntity] = await Promise.all([
        getActivationData(),
        getAppSettingsData(),
        getPhoneBooksEntity(),
      ]);
      const plan = (activation?.plan ?? "").toUpperCase();
      const menuButton = getMenuButtonConfig(appSettings?.menuButtons ?? [], companyId);
      const resolvedButtonName = resolveButtonTitle(
        appSettings?.menuWords ?? [],
        buttonType,
        language,
        companyId
      );
      const resolvedGroupId = resolveGroupIdFromDisplayId(phoneBooksEntity, companyId);

      if (!menuButton) {
        navigateToError(COMPANY_SETTINGS_NOT_FOUND);
        return;
      }

      const notificationType = resolveNotificationType(buttonType, menuButton)?.toUpperCase();
      if (!notificationType) {
        navigateToError(COMPANY_SETTINGS_NOT_FOUND);
        return;
      }

      const destination = resolveCallDestination(buttonType, menuButton);
      const destinationConfigured = hasCallDestination(destination);

      const handledCallFlow = handleCallFlow(
        buttonType,
        plan,
        notificationType,
        menuButton,
        destinationConfigured
      );
      if (handledCallFlow) {
        return;
      }

      const handledChatFlow = await handleChatFlow(
        buttonType,
        plan,
        notificationType,
        destinationConfigured,
        resolvedButtonName,
        resolvedGroupId
      );
      if (handledChatFlow) {
        return;
      }

      navigateToError(COMPANY_SETTINGS_NOT_FOUND);
    } catch {
      navigateToError(DEFAULT_ERROR_CODE);
    }
  };

  return (
    <div className="reception-visitor-info__content">
      <h2 className="screen__title">{t("visitorInfo.title")}</h2>
      <div className="reception-visitor-info__input-card">
        <div className="reception-visitor-info__input-row flex-row">
          <label htmlFor="companyName" className="reception-visitor-info__label">
            {localLabels.company ?? ""}
            {effectiveRequired.company ? (
              <span className="reception-visitor-info__required-mark">
                {t("receptionRequiredMark")}
              </span>
            ) : null}
          </label>

          <div className="reception-visitor-info__field">
            <input
              id="companyName"
              ref={firstInputRef}
              className="reception-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={placeholderCompany || localLabels.company || ""}
              aria-required={effectiveRequired.company}
            />

            {companyTooLong ? (
              <div className="reception-visitor-info__error" role="alert">
                {t("visitorInfo.maxLengthValidation.first")}
              </div>
            ) : null}
          </div>
        </div>

        <div className="reception-visitor-info__input-row flex-row">
          <label htmlFor="visitorName" className="reception-visitor-info__label">
            {localLabels.visitorName ?? ""}
            {effectiveRequired.visitorName ? (
              <span className="reception-visitor-info__required-mark">
                {t("receptionRequiredMark")}
              </span>
            ) : null}
          </label>

          <div className="reception-visitor-info__field">
            <input
              id="visitorName"
              className="reception-input"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              placeholder={placeholderFullName || localLabels.visitorName || ""}
              aria-required={effectiveRequired.visitorName}
            />

            {visitorNameTooLong ? (
              <div className="reception-visitor-info__error" role="alert">
                {t("visitorInfo.maxLengthValidation.second")}
              </div>
            ) : null}
          </div>
        </div>

        {shouldShowRequirement ? (
          <div className="reception-visitor-info__input-row flex-row">
            <label htmlFor="requirement" className="reception-visitor-info__label">
              {localLabels.requirement ?? ""}
              {effectiveRequired.requirement ? (
                <span className="reception-visitor-info__required-mark">
                  {t("receptionRequiredMark")}
                </span>
              ) : null}
            </label>

            <div className="reception-visitor-info__field">
              <input
                id="requirement"
                className="reception-input"
                value={requirement}
                onChange={(e) => setRequirement(e.target.value)}
                placeholder={placeholderRequirement || localLabels.requirement || ""}
                aria-required={effectiveRequired.requirement}
              />

              {requirementTooLong ? (
                <div className="reception-visitor-info__error" role="alert">
                  {t("visitorInfo.maxLengthValidation.third")}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="reception-visitor-info__input-row flex-row reception-visitor-info__actions">
          <button
            className="text-btn gradient-primary-btn"
            onClick={handleOk}
            disabled={!isValid() || isSubmitting}
          >
            <span>{t("ok")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisitorInfoInputScreen;
